import { useState, useEffect } from "react";
import api from "../../services/api";
import { toast } from "react-toastify";
import { FiEye, FiEyeOff, FiFileText, FiHelpCircle, FiAlertCircle } from "react-icons/fi";
import { ConfiguracaoEmpresa, ServicoNFSe, Campo } from "../../services/tipos";
import Listagem from "../../components/listagem";
import ModalForm from "../../components/modal-form";
import { TipoFormulario } from "../../services/constantes";
import { FaFileInvoice } from "react-icons/fa";
import { confirm } from "../../components/confirm";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";

interface ServicoMunicipalOption {
    id: string;
    description: string;
    issTax?: number;
}

const BuscaServicoMunicipal: React.FC<{
    value: any;
    onChange: (val: any) => void;
    onServicoSelecionado: (cnae: string, codTrib: string, descricao: string, issTax: number) => void;
}> = ({ value, onChange, onServicoSelecionado }) => {
    const [options, setOptions] = useState<ServicoMunicipalOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [typing, setTyping] = useState(false);
    const [localInput, setLocalInput] = useState('');
    const [timer, setTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

    const removerAcentos = (str: string) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    const buscar = async (term: string) => {
        if (term.length < 2) { setOptions([]); return; }
        setLoading(true);
        try {
            const termSemAcento = removerAcentos(term);
            const res = await api.get(`/ConfiguracaoEmpresa/ServicosMunicipais?description=${encodeURIComponent(termSemAcento)}`);
            const data = res.data?.data || res.data || [];
            setOptions(Array.isArray(data) ? data : []);
        } catch (err) { console.error('Erro ao buscar serviços municipais:', err); setOptions([]); }
        finally { setLoading(false); }
    };

    const displayText = typing ? localInput : (value || '');

    return (
        <Autocomplete
            size="small"
            options={options}
            loading={loading}
            value={null}
            inputValue={displayText}
            onInputChange={(_e, newInput, reason) => {
                if (reason === 'input') {
                    setTyping(true);
                    setLocalInput(newInput);
                    if (timer) clearTimeout(timer);
                    if (newInput.length >= 2) setTimer(setTimeout(() => buscar(newInput), 400));
                } else if (reason === 'clear') {
                    setTyping(false);
                    setLocalInput('');
                    onChange('');
                }
            }}
            onChange={(_e, newVal: any) => {
                if (!newVal) return;
                const desc = newVal.description || '';
                const issTax = newVal.issTax || 0;
                setTyping(false);
                setLocalInput('');
                onChange(desc);
                const match = desc.match(/^([\d.]+)\s*\|\s*([\d.]+)\s*-\s*(.+)$/);
                if (match) {
                    const codTribNacional = match[1];
                    const cnae = codTribNacional.substring(0, 7);
                    onServicoSelecionado(cnae, codTribNacional, match[3].trim().toUpperCase(), issTax);
                } else {
                    const partes = desc.split(' - ');
                    const descricaoTexto = (partes.length > 1 ? partes.slice(1).join(' - ') : desc).trim().toUpperCase();
                    const codTrib = (partes[0] || '').split('|')[0].trim();
                    onServicoSelecionado(codTrib.substring(0, 7), codTrib, descricaoTexto, issTax);
                }
            }}
            getOptionLabel={(opt: any) => typeof opt === 'string' ? opt : opt.description || ''}
            renderOption={(props, opt: any) => (
                <li {...props} key={opt.id}>
                    <span style={{ fontSize: '0.85rem' }}>{opt.description}</span>
                </li>
            )}
            filterOptions={(x) => x}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder="Digite para buscar (ex: locação, 9903, transporte...)"
                    variant="outlined"
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading && <CircularProgress color="inherit" size={18} />}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
            noOptionsText={displayText.length < 2 ? "Digite pelo menos 2 caracteres..." : "Nenhum serviço encontrado"}
            freeSolo
        />
    );
};

interface NfseTabProps {
    empresaId: string;
    initialData: ConfiguracaoEmpresa;
    emissaoNotaFiscalLiberada?: boolean;
    empresaDocumento?: string;
    empresaNome?: string;
    onEmpresaAtualizada?: () => void;
}

export const NfseTab: React.FC<NfseTabProps> = ({ empresaId, initialData, emissaoNotaFiscalLiberada, empresaDocumento, empresaNome, onEmpresaAtualizada }) => {
    const [loading, setLoading] = useState(false);
    const [loadingServicos, setLoadingServicos] = useState(false);
    const [mostrarSenha, setMostrarSenha] = useState(false);
    const [activeSubTab, setActiveSubTab] = useState('configuracao');
    const [servicos, setServicos] = useState<ServicoNFSe[]>([]);
    const [showModalServico, setShowModalServico] = useState(false);
    const [servicoSelecionado, setServicoSelecionado] = useState<ServicoNFSe>({} as ServicoNFSe);
    const [modoForm, setModoForm] = useState<TipoFormulario>(TipoFormulario.tpNenhum);
    const [realizarConsultaServicos, setRealizarConsultaServicos] = useState(true);
    const [entidadeParaAtualizacao, setEntidadeParaAtualizacao] = useState<any>({});
    const [mostrarAvancado, setMostrarAvancado] = useState(false);
    const [forcarAtualizacao, setForcarAtualizacao] = useState<boolean>(false);
    const [cst, setCST] = useState("");

    const [config, setConfig] = useState<ConfiguracaoEmpresa>({
        ...initialData,
        ambiente: initialData.ambiente ?? 1,
        regimeEspecialTributacao: initialData.regimeEspecialTributacao ?? 0,
        tipoTributacaoServico: initialData.tipoTributacaoServico ?? 1,
        proximoNumeroLote: initialData.proximoNumeroLote || 1,
        proximoNumeroRps: initialData.proximoNumeroRps || 1,
        padraoNacional: initialData.padraoNacional ?? false,
        usarServicoPadraoNFSe: initialData.usarServicoPadraoNFSe ?? false,
    });

    const colunasServico = [
        { field: 'codigo', headerName: 'Código', flex: 1 },
        { field: 'codigoCnae', headerName: 'Código CNAE', flex: 1 },
        { field: 'codigoTributacaoNacional', headerName: 'Trib. Nacional', flex: 1 },
        { field: 'codigoTributacaoMunicipio', headerName: 'Trib. Município', flex: 1 },
        { field: 'descricao', headerName: 'Descrição', flex: 2 },
        { field: 'aliquotaIss', headerName: 'ISS', flex: 0.8, valueFormatter: (params: any) => `${params.value}%` },
        { field: 'aliquotaIbsEstadual', headerName: 'IBS Est.', flex: 0.8, valueFormatter: (params: any) => `${params.value || 0}%` },
        { field: 'aliquotaIbsMunicipal', headerName: 'IBS Mun.', flex: 0.8, valueFormatter: (params: any) => `${params.value || 0}%` },
        { field: 'aliquotaCbs', headerName: 'CBS', flex: 0.8, valueFormatter: (params: any) => `${params.value || 0}%` },
    ];

    const camposServico: Campo[] = [
        // === CAMPOS PRINCIPAIS ===
        {
            linha: 1, coluna: 1, largura: 12,
            nome: "codigo",
            label: "Código de Serviço Municipal",
            tipo: "custom",
            required: false,
            maximoCaracteres: 20,
            component: ({ value, onChange }) => (
                <BuscaServicoMunicipal
                    value={value}
                    onChange={onChange}  /* salva descrição completa no campo codigo */
                    onServicoSelecionado={(cnae, codTrib, descricao, issTax) => {
                        setEntidadeParaAtualizacao((prev: any) => ({
                            ...prev,
                            codigoCnae: cnae,
                            codigoTributacaoMunicipio: codTrib,
                            descricao: descricao,
                            aliquotaIss: issTax || 0,
                        }));
                        setForcarAtualizacao(true);
                    }}
                />
            ),
        },
        {
            linha: 2, coluna: 1, largura: 4,
            nome: "aliquotaIss",
            label: "Alíquota ISS (%)",
            tipo: "percentual",
            required: false,
            ajudinha: {
                texto: "Percentual de ISS aplicado ao serviço. Preenchido automaticamente, ajuste conforme seu município."
            }
        },
        {
            linha: 2, coluna: 2, largura: 4,
            nome: "padrao",
            label: "Serviço Padrão",
            tipo: "checkbox",
            required: false,
            ajudinha: {
                texto: "Marque para usar este serviço como padrão nas emissões de notas."
            }
        },
        {
            linha: 3, coluna: 1, largura: 12,
            nome: "descricao",
            label: "Descrição do Serviço",
            tipo: "memo",
            required: false,
            tudoMaiusculo: true,
            maximoCaracteres: 255,
            ajudinha: {
                texto: "Preenchida automaticamente. Edite se necessário."
            }
        },
        // === CAMPOS AVANÇADOS (ocultos por padrão) ===
        {
            linha: 4, coluna: 1, largura: 12,
            nome: "_toggleAvancado",
            label: "",
            tipo: "custom",
            required: false,
            component: () => (
                <button
                    type="button"
                    onClick={() => setMostrarAvancado(!mostrarAvancado)}
                    style={{ background: 'none', border: 'none', color: '#1F467E', cursor: 'pointer', fontSize: '1rem', padding: 0, textDecoration: 'underline' }}
                >
                    {mostrarAvancado ? 'Ocultar configurações avançadas' : 'Mostrar configurações avançadas (Reforma Tributária e mais)'}
                </button>
            ),
        },
        {
            linha: 5, coluna: 1, largura: 3,
            nome: "codigoCnae",
            label: "Código CNAE",
            tipo: "text",
            required: false,
            maximoCaracteres: 20,
            visible: () => mostrarAvancado,
        },
        {
            linha: 5, coluna: 2, largura: 3,
            nome: "codigoTributacaoNacional",
            label: "Cód. Tributação Nacional",
            tipo: "text",
            required: false,
            maximoCaracteres: 20,
            visible: () => mostrarAvancado,
        },
        {
            linha: 5, coluna: 3, largura: 3,
            nome: "codigoTributacaoMunicipio",
            label: "Cód. Tributação Municipal",
            tipo: "text",
            required: false,
            maximoCaracteres: 20,
            visible: () => mostrarAvancado,
        },
        {
            linha: 6, coluna: 1, largura: 3,
            nome: "codigoNbs",
            label: "Código NBS",
            tipo: "text",
            required: false,
            maximoCaracteres: 20,
            visible: () => mostrarAvancado,
        },
        {
            linha: 7, coluna: 1, largura: 3,
            nome: "indOp",
            label: "Ind. Operação",
            tipo: "text",
            required: false,
            maximoCaracteres: 20,
            visible: () => mostrarAvancado,
        },
        {
            linha: 7, coluna: 2, largura: 3,
            nome: "cst",
            label: "Situação Tributária (CST)",
            tipo: "text",
            required: false,
            visible: () => mostrarAvancado,
        },
        {
            linha: 7, coluna: 3, largura: 3,
            nome: "cClassTrib",
            label: "Classificação Tributária",
            tipo: "text",
            required: false,
            visible: () => mostrarAvancado,
        },
        {
            linha: 7, coluna: 4, largura: 3,
            nome: "tipoRetencaoIss",
            label: "Tipo Retenção ISS",
            tipo: "select",
            required: false,
            visible: () => mostrarAvancado,
            opcoes: [
                { valor: 0, label: "0 - Retenção" },
                { valor: 1, label: "1 - Normal" },
                { valor: 2, label: "2 - Substituição" },
                { valor: 3, label: "3 - Nenhum" },
                { valor: 4, label: "4 - Retido Fora Mun." },
                { valor: 5, label: "5 - Devido Fora Não Ret." }
            ]
        },
        {
            linha: 8, coluna: 1, largura: 3,
            nome: "aliquotaCbs",
            label: "Alíquota CBS (%)",
            tipo: "percentual",
            required: false,
            visible: () => mostrarAvancado,
        },
        {
            linha: 8, coluna: 2, largura: 3,
            nome: "aliquotaIbsEstadual",
            label: "IBS Estadual (%)",
            tipo: "percentual",
            required: false,
            visible: () => mostrarAvancado,
        },
        {
            linha: 8, coluna: 3, largura: 3,
            nome: "aliquotaIbsMunicipal",
            label: "IBS Municipal (%)",
            tipo: "percentual",
            required: false,
            visible: () => mostrarAvancado,
        },
        {
            linha: 8, coluna: 4, largura: 3,
            nome: "responsavelRetencao",
            label: "Resp. Retenção",
            tipo: "select",
            required: false,
            visible: () => mostrarAvancado,
            opcoes: [
                { valor: 0, label: "Tomador" },
                { valor: 1, label: "Prestador" },
                { valor: 2, label: "Intermediário" },
                { valor: 3, label: "Nenhum" }
            ]
        },
        {
            linha: 9, coluna: 1, largura: 3,
            nome: "desconto",
            label: "Desconto (R$)",
            tipo: "decimal",
            required: false,
            visible: () => mostrarAvancado,
        },
        {
            linha: 9, coluna: 2, largura: 3,
            nome: "descontoIncondicionado",
            label: "Desconto Incondicionado (R$)",
            tipo: "decimal",
            required: false,
            visible: () => mostrarAvancado,
        }
    ];

    useEffect(() => {
        if (forcarAtualizacao) setForcarAtualizacao(false);
    }, [forcarAtualizacao]);

    useEffect(() => {
        const fetchServicos = async () => {
            if (activeSubTab === 'servicos' && realizarConsultaServicos) {
                setLoadingServicos(true);
                try {
                    const response = await api.get(`/ServicoNFSePorEmpresa/${empresaId}`);
                    setServicos(response.data);
                } catch (error) {
                    toast.error("Erro ao carregar serviços de NFSe");
                } finally {
                    setLoadingServicos(false);
                    setRealizarConsultaServicos(false);
                }
            }
        };
        fetchServicos();
    }, [activeSubTab, realizarConsultaServicos, empresaId]);

    const extrairDadosCertificado = async (certBase64: string, senhaBase64: string) => {
        if (!certBase64 || !senhaBase64) return;
        try {
            const res = await api.post('/ConfiguracaoEmpresa/ExtrairDadosCertificado', {
                certificadoBase64: certBase64,
                senhaBase64: senhaBase64,
            });
            const { codigoMunicipio, razaoSocial, municipio, uf } = res.data;
            if (codigoMunicipio) {
                setConfig(prev => ({
                    ...prev,
                    codigoMunicipio: prev.codigoMunicipio || codigoMunicipio,
                }));
                toast.success(`Código IBGE preenchido automaticamente: ${codigoMunicipio} (${municipio}/${uf})`);
            }
        } catch (err: any) {
            const msg = err.response?.data?.mensagem;
            if (msg) toast.warn(msg);
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                const arrayBuffer = event.target?.result as ArrayBuffer;
                let binary = '';
                const bytes = new Uint8Array(arrayBuffer);
                for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
                const certBase64 = window.btoa(binary);
                setConfig(prev => {
                    // Se já tem senha, extrair dados automaticamente
                    if (prev.certificadoDigitalSenha) {
                        extrairDadosCertificado(certBase64, prev.certificadoDigitalSenha);
                    }
                    return { ...prev, certificadoDigitalArquivo: certBase64 };
                });
            };
            reader.readAsArrayBuffer(file);
        }
    };

    const handleSaveConfig = async () => {
        try {
            setLoading(true);
            await api.put(`/ConfiguracaoEmpresa/AtualizarConfiguracaoNfse/${empresaId}`, { ...config, idEmpresa: empresaId });
            toast.success('Configurações de NFS-e atualizadas com sucesso!');

            // Se o documento da empresa é CPF (11 dígitos), verificar se o certificado tem CNPJ
            const docLimpo = (empresaDocumento || '').replace(/\D/g, '');
            if (docLimpo.length === 11 && config.certificadoDigitalArquivo && config.certificadoDigitalSenha) {
                try {
                    const res = await api.post('/ConfiguracaoEmpresa/ExtrairDadosCertificado', {
                        certificadoBase64: config.certificadoDigitalArquivo,
                        senhaBase64: config.certificadoDigitalSenha,
                    });
                    const { cnpj, razaoSocial } = res.data;
                    if (cnpj) {
                        const cnpjFormatado = cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
                        const confirmado = await confirm(
                            `Seu cadastro está com CPF, mas para emitir NFS-e é necessário CNPJ.\n\n` +
                            `O certificado digital contém:\n` +
                            `CNPJ: ${cnpjFormatado}\n` +
                            `Razão Social: ${razaoSocial || 'Não identificada'}\n\n` +
                            `Deseja atualizar o documento e a razão social da empresa?`
                        );
                        if (confirmado) {
                            await api.put(`/ConfiguracaoEmpresa/AtualizarDocumentoEmpresaPorCertificado/${empresaId}`, {
                                cnpj,
                                razaoSocial: razaoSocial || empresaNome,
                            });
                            toast.success('Documento e razão social atualizados com sucesso!');
                            onEmpresaAtualizada?.();
                        }
                    }
                } catch (err: any) {
                    // Não bloquear o fluxo se a extração falhar
                    console.warn('Não foi possível verificar CNPJ do certificado:', err);
                }
            }
        } catch (error: any) {
            toast.error(error.response?.data?.Mensagem || 'Erro ao salvar as configurações');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target as HTMLInputElement;
        const val = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
        setConfig(prev => ({ ...prev, [name]: type === 'number' ? Number(value) : val }));
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        try {
            const senhaBase64 = window.btoa(e.target.value);
            setConfig(prev => ({ ...prev, certificadoDigitalSenha: senhaBase64 }));
        } catch (error) {
            console.error("Erro ao converter senha para Base64");
        }
    };

    const handlePasswordBlur = () => {
        if (config.certificadoDigitalArquivo && config.certificadoDigitalSenha) {
            extrairDadosCertificado(config.certificadoDigitalArquivo, config.certificadoDigitalSenha);
        }
    };

    const getSenhaDecodificada = () => {
        if (!config.certificadoDigitalSenha) return '';
        try {
            return window.atob(config.certificadoDigitalSenha);
        } catch (error) {
            return config.certificadoDigitalSenha;
        }
    };

    const handleCriarServico = () => {
        setModoForm(TipoFormulario.tpCadastro);
        setServicoSelecionado({
            codigo: '771100001 | 99.03 - Locação de automóveis sem condutor.',
            codigoCnae: '7711000',
            codigoTributacaoMunicipio: '771100001',
            descricao: 'LOCAÇÃO DE AUTOMÓVEIS SEM CONDUTOR.',
            aliquotaIss: 0,
            padrao: servicos.length === 0,
        } as any);
        setShowModalServico(true);
    };

    const handleEditarServico = (servico: any) => {
        setModoForm(TipoFormulario.tpEdicao);
        setServicoSelecionado(servico);
        setShowModalServico(true);
    };

    const handleVisualizarServico = (servico: any) => {
        setModoForm(TipoFormulario.tpVisualizacao);
        setServicoSelecionado(servico);
        setShowModalServico(true);
    };

    const handleExcluirServico = async (servico: any) => {
        setLoadingServicos(true);
        try {
            await api.delete(`/ServicoNFSe/${servico.id}`);
            toast.success("Serviço excluído com sucesso");
            setRealizarConsultaServicos(true);
        } catch (error: any) {
            toast.error("Erro ao excluir serviço");
        } finally {
            setLoadingServicos(false);
        }
    };

    const handleSalvarServico = async (dados: any) => {
        if (dados.padrao && servicos.some(s => s.padrao && s.id !== dados.id)) {
            const confirmado = await confirm("Já existe um serviço padrão, deseja substituí-lo?");
            if (!confirmado) return;
        }
        setLoadingServicos(true);
        try {
            const { _toggleAvancado, ...dadosLimpos } = dados;
            const descricaoCompleta = (dadosLimpos.codigo || '').toString();
            const codigoMatch = descricaoCompleta.match(/\|\s*([\d.]+)\s*-/);
            const partesFiscais = descricaoCompleta.match(/^([\d.]+)\s*\|\s*([\d.]+)\s*-\s*(.+)$/);
            const codTribNacional = partesFiscais ? partesFiscais[1] : (descricaoCompleta.split('|')[0] || '').trim();
            const codigoServico = (codigoMatch ? codigoMatch[1] : descricaoCompleta).substring(0, 20);
            const descricaoServico = (dadosLimpos.descricao || descricaoCompleta.split(' - ').slice(1).join(' - ') || descricaoCompleta).toString().trim().toUpperCase();
            const payload = {
                ...dadosLimpos, idEmpresa: empresaId,
                codigo: codigoServico,
                descricao: descricaoServico,
                codigoTributacaoMunicipio: ((dadosLimpos.codigoTributacaoMunicipio || codTribNacional).toString()).substring(0, 20),
                codigoCnae: ((dadosLimpos.codigoCnae || codTribNacional).toString()).substring(0, 20),
                aliquotaIss: dados.aliquotaIss || 0,
                aliquotaIbsEstadual: dados.aliquotaIbsEstadual || 0,
                aliquotaIbsMunicipal: dados.aliquotaIbsMunicipal || 0,
                aliquotaCbs: dados.aliquotaCbs || 0,
                desconto: dados.desconto || 0,
                descontoIncondicionado: dados.descontoIncondicionado || 0,
                tipoRetencaoIss: isNaN(Number(dados.tipoRetencaoIss)) ? 1 : Number(dados.tipoRetencaoIss),
                responsavelRetencao: isNaN(Number(dados.responsavelRetencao)) ? 0 : Number(dados.responsavelRetencao),
                padrao: dados.padrao || false
            };
            if (modoForm === TipoFormulario.tpCadastro) await api.post('/ServicoNFSe', payload);
            else await api.put('/ServicoNFSe', payload);
            setShowModalServico(false);
            setRealizarConsultaServicos(true);
            toast.success("Serviço salvo com sucesso");
        } catch (error: any) {
            const data = error.response?.data;
            const erros = data?.errors;
            const msgValidacao = erros
                ? Object.keys(erros).map((k) => Array.isArray(erros[k]) ? erros[k].join(' ') : String(erros[k])).join(' ')
                : null;
            toast.error(data?.Mensagem || msgValidacao || data?.title || "Erro ao processar");
        } finally {
            setLoadingServicos(false);
        }
    };

    return (
        <div className="bg-white dark:bg-gray-900 p-6 rounded-lg relative overflow-hidden min-h-[400px]">
            {!emissaoNotaFiscalLiberada && (
                <div className="absolute inset-0 z-30 flex items-center justify-center bg-gray-100/80 dark:bg-gray-950/80 rounded-lg">
                    <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col items-center text-center max-w-sm mx-4">
                        <div className="w-16 h-16 bg-blue-100 dark:bg-[#1F467E]/20 rounded-full flex items-center justify-center mb-4">
                            <FiAlertCircle size={32} className="text-[#1F467E] dark:text-blue-400" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-black-100 mb-2">Módulo em Teste Beta</h3>
                        <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-6 text-sm">
                            A emissão de Notas Fiscais (NFS-e) ainda não está liberada para sua empresa.
                        </p>
                    </div>
                </div>
            )}

            <div className={!emissaoNotaFiscalLiberada ? "opacity-10 pointer-events-none select-none" : ""}>
                <h2 className="text-xl font-semibold text-black dark:text-gray-100 mb-6 flex items-center">
                    <FiFileText className="mr-2 text-[#1F467E]" /> Notas Fiscais (NFS-e)
                </h2>

                <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6">
                    <button
                        className={`px-4 py-2 font-medium text-sm transition-colors ${activeSubTab === 'configuracao' ? 'text-[#1F467E] border-b-2 border-[#1F467E]' : 'text-gray-500 hover:text-black dark:hover:text-gray-300'}`}
                        onClick={() => setActiveSubTab('configuracao')}
                    >
                        Configuração
                    </button>
                    <button
                        className={`px-4 py-2 font-medium text-sm transition-colors ${activeSubTab === 'servicos' ? 'text-[#1F467E] border-b-2 border-[#1F467E]' : 'text-gray-500 hover:text-black dark:hover:text-gray-300'}`}
                        onClick={() => setActiveSubTab('servicos')}
                    >
                        Serviços
                    </button>
                </div>

                {activeSubTab === 'configuracao' && (
                    <div className="flex flex-col lg:flex-row gap-6 items-start">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl flex-1">
                            <div className="space-y-4 border p-4 rounded-md dark:border-gray-700">
                                <h3 className="font-medium text-black dark:text-gray-100">Certificado Digital</h3>
                                <div>
                                    <label className="block text-sm font-medium mb-1 text-black dark:text-gray-300">Arquivo (.pfx)</label>
                                    <input type="file" accept=".pfx" onChange={handleFileChange} className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[#1F467E] file:text-white" />
                                    {config.certificadoDigitalArquivo && <p className="text-xs text-green-600 mt-1 flex items-center">✓ Certificado carregado</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1 text-black dark:text-gray-300">Senha do Certificado</label>
                                    <div className="relative">
                                        <input type={mostrarSenha ? "text" : "password"} name="certificadoDigitalSenha" value={getSenhaDecodificada()} onChange={handlePasswordChange} onBlur={handlePasswordBlur} className="w-full p-2 border border-gray-300 rounded-md bg-white dark:bg-gray-800" />
                                        <button type="button" onClick={() => setMostrarSenha(!mostrarSenha)} className="absolute right-2 top-2 text-gray-400">{mostrarSenha ? <FiEyeOff /> : <FiEye />}</button>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1 text-black dark:text-gray-300 flex items-center">
                                        Chave de Autorização
                                        <span className="ml-1 relative group">
                                            <FiHelpCircle className="text-gray-400 cursor-help" size={14} />
                                            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block bg-gray-800 text-white text-xs rounded px-2 py-1 w-56 text-center z-50">
                                                Código exigido por algumas prefeituras. Caso a sua prefeitura não disponibilize, não é necessário informar.
                                            </span>
                                        </span>
                                    </label>
                                    <input type="text" name="wsChaveAutorizacao" value={config.wsChaveAutorizacao || ''} onChange={handleChange} placeholder="Informe se exigido pela prefeitura" className="w-full p-2 border border-gray-300 rounded-md bg-white dark:bg-gray-800 text-sm" />
                                </div>
                            </div>

                            <div className="space-y-4 border p-4 rounded-md dark:border-gray-700">
                                <h3 className="font-medium text-black dark:text-gray-100">Configurações de Emissão</h3>
                                <div className="flex items-center space-x-4 mb-4">
                                    <label className="flex items-center cursor-pointer">
                                        <input type="checkbox" name="padraoNacional" checked={config.padraoNacional} onChange={handleChange} className="w-4 h-4 text-[#1F467E] rounded" />
                                        <span className="ml-2 text-sm font-medium text-black dark:text-gray-300">Usar Padrão Nacional</span>
                                    </label>
                                </div>
                                <div className="flex items-center mb-4">
                                    <label className="flex items-center cursor-pointer">
                                        <input type="checkbox" name="usarServicoPadraoNFSe" checked={config.usarServicoPadraoNFSe} onChange={handleChange} className="w-4 h-4 text-[#1F467E] rounded" />
                                        <span className="ml-2 text-sm font-medium text-black dark:text-gray-300">Marque para utilizar o Serviço padrão nas emissões</span>
                                    </label>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1 text-black dark:text-gray-300">Ambiente</label>
                                    <select name="ambiente" value={config.ambiente} onChange={handleChange} className="w-full p-2 border border-gray-300 rounded-md bg-white dark:bg-gray-800 text-sm">
                                        <option value={0}>Produção</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1 text-black dark:text-gray-300">Código IBGE</label>
                                    <input type="text" name="codigoMunicipio" value={config.codigoMunicipio || ''} onChange={handleChange} placeholder="Ex: 2304400" className="w-full p-2 border border-gray-300 rounded-md bg-white dark:bg-gray-800 text-sm" />
                                </div>
                            </div>

                            <div className="space-y-4 border p-4 rounded-md dark:border-gray-700">
                                <h3 className="font-medium text-black dark:text-gray-100">Dados da Empresa</h3>
                                <div>
                                    <label className="block text-sm font-medium mb-1 text-black dark:text-gray-300">Inscrição Municipal</label>
                                    <input type="text" name="inscricaoMunicipal" value={config.inscricaoMunicipal || ''} onChange={handleChange} className="w-full p-2 border border-gray-300 rounded-md bg-white dark:bg-gray-800 text-sm" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1 text-black dark:text-gray-300">Regime Tributação</label>
                                    <select name="regimeEspecialTributacao" value={config.regimeEspecialTributacao} onChange={handleChange} className="w-full p-2 border border-gray-300 rounded-md bg-white dark:bg-gray-800 text-xs">
                                        <option value={0}>0 - Nenhum</option>
                                        <option value={1}>1 - Microempresa Municipal</option>
                                        <option value={2}>2 - Estimativa</option>
                                        <option value={3}>3 - Sociedade de Profissionais</option>
                                        <option value={4}>4 - Cooperativa</option>
                                        <option value={5}>5 - Microempresário Individual</option>
                                        <option value={6}>6 - Microempresário Empresa PP</option>
                                        <option value={7}>7 - Lucro Real</option>
                                        <option value={8}>8 - Lucro Presumido</option>
                                        <option value={9}>9 - Simples Nacional</option>
                                        <option value={10}>10 - Imune</option>
                                        <option value={11}>11 - Empresa Individual RELI</option>
                                        <option value={12}>12 - Empresa PP</option>
                                        <option value={13}>13 - Microempresário</option>
                                        <option value={14}>14 - Outros</option>
                                        <option value={15}>15 - Movimento Mensal</option>
                                        <option value={16}>16 - ISSQN Autônomos</option>
                                        <option value={17}>17 - ISSQN Sociedade</option>
                                        <option value={18}>18 - Notário Registrador</option>
                                        <option value={19}>19 - Tributação por Faturamento Variável</option>
                                        <option value={20}>20 - Fixo</option>
                                        <option value={21}>21 - Isenção</option>
                                        <option value={22}>22 - Exigibilidade Suspensa Judicial</option>
                                        <option value={23}>23 - Exigibilidade Suspensa Administrativa</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1 text-black dark:text-gray-300">Tipo Tributação Serviço</label>
                                    <select name="tipoTributacaoServico" value={config.tipoTributacaoServico} onChange={handleChange} className="w-full p-2 border border-gray-300 rounded-md bg-white dark:bg-gray-800 text-xs">
                                        <option value={1}>1 - No Município</option>
                                        <option value={2}>2 - Fora do Município</option>
                                        <option value={3}>3 - Isenta</option>
                                        <option value={4}>4 - Imune</option>
                                        <option value={5}>5 - Suspensa por Decisão Judicial</option>
                                        <option value={6}>6 - Suspensa por Procedimento Administrativo</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1 text-black dark:text-gray-300">Natureza Operação</label>
                                    <select name="naturezaOperacao" value={config.naturezaOperacao || 7} onChange={handleChange} className="w-full p-2 border border-gray-300 rounded-md bg-white dark:bg-gray-800 text-xs">
                                        <option value={1}>1 - Tributação no município</option>
                                        <option value={2}>2 - Tributação fora do município</option>
                                        <option value={3}>3 - Isenção</option>
                                        <option value={4}>4 - Imune</option>
                                        <option value={5}>5 - Exigibilidade suspensa judicial</option>
                                        <option value={6}>6 - Exigibilidade suspensa administrativa</option>
                                        <option value={7}>7 - Sem dedução</option>
                                    </select>
                                </div>
                            </div>

                            <div className="space-y-4 border p-4 rounded-md dark:border-gray-700">
                                <h3 className="font-medium text-black dark:text-gray-100">Numeração</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1 text-black dark:text-gray-300">Série RPS</label>
                                        <input type="text" name="serieRps" value={config.serieRps || ''} onChange={handleChange} className="w-full p-2 border border-gray-300 rounded-md bg-white dark:bg-gray-800 text-sm" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1 text-black dark:text-gray-300">Próximo RPS</label>
                                        <input type="number" name="proximoNumeroRps" value={config.proximoNumeroRps} onChange={handleChange} className="w-full p-2 border border-gray-300 rounded-md bg-white dark:bg-gray-800 text-sm" />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1 text-black dark:text-gray-300">Próximo Lote</label>
                                    <input type="number" name="proximoNumeroLote" value={config.proximoNumeroLote} onChange={handleChange} className="w-full p-2 border border-gray-300 rounded-md bg-white dark:bg-gray-800 text-sm" />
                                </div>
                            </div>
                        </div>
                        <div className="w-full lg:w-auto mt-4 lg:mt-0">
                            <button onClick={handleSaveConfig} disabled={loading} className="w-full px-8 py-3 bg-[#1F467E] text-white font-bold rounded-lg hover:bg-[#15325b] transition-all shadow-md disabled:opacity-50">
                                {loading ? 'Salvando...' : 'Salvar Configurações NFS-e'}
                            </button>
                        </div>
                    </div>
                )}

                {activeSubTab === 'servicos' && (
                    <div className="space-y-6">
                        <Listagem linhas={servicos} colunas={colunasServico} nomeEntidade="Serviço NFSe" nomePropriedade="descricao" carregando={loadingServicos} IconeEntidade={FaFileInvoice} exibirAcoesDeCrud={true} handleCriar={handleCriarServico} handleEditar={handleEditarServico} handleExclusao={handleExcluirServico} handleVisualizacao={handleVisualizarServico} handleSelecionarEntidade={setServicoSelecionado} />
                        <ModalForm
                            show={showModalServico} modo={modoForm} tamanho="xl" campos={camposServico} carregando={loadingServicos} onHide={() => setShowModalServico(false)} onCancel={() => setShowModalServico(false)} entidadeSelecionada={servicoSelecionado} onSave={handleSalvarServico}
                            apiConfig={{
                                endpoint: "ServicoNFSe", idTenentCampo: 'idEmpresa', requisicaoBemSucedida(response) {

                                }, tratarErro(error) {

                                },
                            }}
                            atualizacaoEntidade={{ entidadeUsadaParaAtualizacao: entidadeParaAtualizacao, forcarAtualizacao: forcarAtualizacao }}
                            ajudinha={{ texto: "Cadastre aqui os serviços para emissão de NFS-e (Reforma Tributária).", autoCloseTime: 3, ehDestacado: true }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};